
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "exporting-questionnaire-submissions"
    }}>{`Exporting questionnaire submissions`}</h1>
    <p>{`Partners can export questionnaire submissions as part of the candidate application workflow.
This functionality is supported for questionnaires created:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/use-cases/job-posting/questionnaires"
          }}>{`Programmatically through the SEEK API`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Manually through the direct job posting flow on the SEEK employer website,
or the now-retired screen questionnaires page`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <p>{`Before you start with questionnaire submission queries, you will need to `}<a parentName="p" {...{
        "href": "/auth/partner-tokens"
      }}>{`request a partner token`}</a>{`.`}</p>
    <h2 {...{
      "id": "data-structure"
    }}>{`Data structure`}</h2>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.3d706951587a76d66a1312b7f4561f3c413492e2.mmd.svg')} title="Questionnaire submission objects" />
    <p>{`A job ad may specify a `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/questionnaires"
      }}>{`questionnaire`}</a>{` to include in SEEK’s Apply Form.
The candidate’s responses to the questionnaire are surfaced through the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile/field/seekQuestionnaireSubmission"
      }}><inlineCode parentName="a">{`seekQuestionnaireSubmission`}</inlineCode>{` field`}</a>{` on the application profile.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionnaireSubmission"
      }}><inlineCode parentName="a">{`ApplicationQuestionnaireSubmission`}</inlineCode></a>{` object allows you to retrieve:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`questionnaire`}</inlineCode>{` that the submission corresponds to`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The list of `}<inlineCode parentName="p">{`responses`}</inlineCode>{` to the components of the questionnaire`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`component`}</inlineCode>{` that a given response corresponds to`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "questionnaire-association"
    }}>{`Questionnaire association`}</h3>
    <p>{`If you manage questionnaires as a holistic collection in your software,
retrieve `}<inlineCode parentName="p">{`questionnaire.id`}</inlineCode>{` to correlate the submission back to the questionnaire ID you have stored from the `}<a parentName="p" {...{
        "href": "/use-cases/job-posting"
      }}>{`Job Posting`}</a>{` use case.`}</p>
    <p>{`If you manage individual questionnaire components in your software,
provide a unique `}<inlineCode parentName="p">{`value`}</inlineCode>{` for each `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionnaireComponentInput"
      }}><inlineCode parentName="a">{`ApplicationQuestionnaireComponentInput`}</inlineCode></a>{` during questionnaire creation.
You can then select `}<inlineCode parentName="p">{`responses[].component.value`}</inlineCode>{` from the submission to correlate each response back to the corresponding questionnaire component in your software.`}</p>
    <h3 {...{
      "id": "preferred-choices--scoring"
    }}>{`Preferred choices & scoring`}</h3>
    <p>{`Each `}<inlineCode parentName="p">{`SingleSelect`}</inlineCode>{` and `}<inlineCode parentName="p">{`MultiSelect`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestion"
      }}><inlineCode parentName="a">{`ApplicationQuestion`}</inlineCode></a>{` specifies a collection of possible choices through the `}<inlineCode parentName="p">{`responseChoice`}</inlineCode>{` field.
An `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionChoice"
      }}><inlineCode parentName="a">{`ApplicationQuestionChoice`}</inlineCode></a>{` can be preferenced by setting its `}<inlineCode parentName="p">{`preferredIndicator`}</inlineCode>{` during questionnaire creation.
Candidate responses are scored against these preferred choices.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionnaireSubmission"
          }}><inlineCode parentName="a">{`ApplicationQuestionnaireSubmission`}</inlineCode></a>{` schema definition details scoring of the overall questionnaire submission`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionResponse"
          }}><inlineCode parentName="a">{`ApplicationQuestionResponse`}</inlineCode></a>{` schema definition details scoring of individual questionnaire components`}</p>
      </li>
    </ul>
    <p>{`These scores are provided as an advisory screening indicator for the hirer.
All candidate applications will be delivered to your software;
SEEK does not support knockout questions and does not reject applications based on score.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/candidateProfile"
      }}><inlineCode parentName="a">{`candidateProfile`}</inlineCode>{` query`}</a>{` to retrieve questionnaire submissions.
You should combine this with the query you use to retrieve `}<a parentName="p" {...{
        "href": "/use-cases/application-export/candidate-profiles"
      }}>{`candidate profiles`}</a>{` and `}<a parentName="p" {...{
        "href": "/use-cases/application-export/attachments"
      }}>{`attachments`}</a>{`.`}</p>
    <h3 {...{
      "id": "candidateprofile"
    }}>{`candidateProfile`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  candidateProfile(id: $id) {\\n    seekQuestionnaireSubmission {\\n      questionnaire {\\n        id {\\n          value\\n        }\\n      }\\n      responses {\\n        componentTypeCode\\n        ... on ApplicationQuestionResponse {\\n          score\\n          component {\\n            value\\n            questionHtml\\n          }\\n          answers {\\n            answer\\n            choice {\\n              value\\n            }\\n          }\\n        }\\n        ... on ApplicationPrivacyConsentResponse {\\n          component {\\n            value\\n            descriptionHtml\\n          }\\n          consentGivenIndicator\\n        }\\n      }\\n      score\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":63,\"column\":1,\"offset\":3004},\"end\":{\"line\":99,\"column\":4,\"offset\":3677},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:candidateProfile:apply:7DtW6Q68gk2R4okNGhvg1Y\\\"\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3679},\"end\":{\"line\":105,\"column\":4,\"offset\":3768},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"candidateProfile\\\": {\\n    \\\"seekQuestionnaireSubmission\\\": {\\n      // The questionnaire presented to the candidate as part of their application\\n      \\\"questionnaire\\\": {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n        }\\n      },\\n\\n      // The candidate's answers to the questionnaire\\n      \\\"responses\\\": [\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"score\\\": 1,\\n          \\\"component\\\": {\\n            \\\"value\\\": null,\\n            \\\"questionHtml\\\": \\\"Which of the following statements best describes your right to work in Australia?\\\"\\n          },\\n          \\\"answers\\\": [\\n            {\\n              \\\"answer\\\": \\\"I'm an Australian citizen\\\",\\n              \\\"choice\\\": {\\n                \\\"value\\\": null\\n              }\\n            }\\n          ]\\n        },\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"score\\\": 1,\\n          \\\"component\\\": {\\n            \\\"value\\\": null,\\n            \\\"questionHtml\\\": \\\"Do you own or have regular access to a car?\\\"\\n          },\\n          \\\"answers\\\": [\\n            {\\n              \\\"answer\\\": \\\"No\\\",\\n              \\\"choice\\\": {\\n                \\\"value\\\": null\\n              }\\n            }\\n          ]\\n        },\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"score\\\": null,\\n          \\\"component\\\": {\\n            \\\"value\\\": null,\\n            \\\"questionHtml\\\": \\\"What do you like most about GraphQL?\\\"\\n          },\\n          \\\"answers\\\": [\\n            {\\n              \\\"answer\\\": \\\"Querying deeply nested objects 😀\\\",\\n              \\\"choice\\\": null\\n            }\\n          ]\\n        },\\n        {\\n          \\\"componentTypeCode\\\": \\\"PrivacyConsent\\\",\\n          \\\"component\\\": {\\n            \\\"value\\\": null,\\n            \\\"descriptionHtml\\\": \\\"Do you agree with our privacy policy?\\\"\\n          },\\n          \\\"consentGivenIndicator\\\": true\\n        }\\n      ],\\n\\n      // The overall score of the questionnaire submission\\n      \\\"score\\\": 1\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":107,\"column\":1,\"offset\":3770},\"end\":{\"line\":181,\"column\":4,\"offset\":5714},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "candidateProfile(id:": true,
        "$id)": true,
        "seekQuestionnaireSubmission": true,
        "questionnaire": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "responses": true,
        "componentTypeCode\\n": true,
        "...": true,
        "on": true,
        "ApplicationQuestionResponse": true,
        "score\\n": true,
        "component": true,
        "questionHtml\\n": true,
        "answers": true,
        "answer\\n": true,
        "choice": true,
        "ApplicationPrivacyConsentResponse": true,
        "descriptionHtml\\n": true,
        "consentGivenIndicator\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":63,\"column\":1,\"offset\":3004},\"end\":{\"line\":99,\"column\":4,\"offset\":3677},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:candidateProfile:apply:7DtW6Q68gk2R4okNGhvg1Y\\\"\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3679},\"end\":{\"line\":105,\"column\":4,\"offset\":3768},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"candidateProfile\\\":": true,
        "\\\"seekQuestionnaireSubmission\\\":": true,
        "//": true,
        "The": true,
        "presented": true,
        "to": true,
        "the": true,
        "candidate": true,
        "as": true,
        "part": true,
        "of": true,
        "their": true,
        "application\\n": true,
        "\\\"questionnaire\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n": true,
        "},\\n\\n": true,
        "candidate's": true,
        "questionnaire\\n": true,
        "\\\"responses\\\":": true,
        "[\\n": true,
        "\\\"componentTypeCode\\\":": true,
        "\\\"Question\\\",\\n": true,
        "\\\"score\\\":": true,
        "1,\\n": true,
        "\\\"component\\\":": true,
        "null,\\n": true,
        "\\\"questionHtml\\\":": true,
        "\\\"Which": true,
        "following": true,
        "statements": true,
        "best": true,
        "describes": true,
        "your": true,
        "right": true,
        "work": true,
        "in": true,
        "Australia?\\\"\\n": true,
        "},\\n": true,
        "\\\"answers\\\":": true,
        "\\\"answer\\\":": true,
        "\\\"I'm": true,
        "an": true,
        "Australian": true,
        "citizen\\\",\\n": true,
        "\\\"choice\\\":": true,
        "null\\n": true,
        "]\\n": true,
        "\\\"Do": true,
        "you": true,
        "own": true,
        "or": true,
        "have": true,
        "regular": true,
        "access": true,
        "a": true,
        "car?\\\"\\n": true,
        "\\\"No\\\",\\n": true,
        "\\\"What": true,
        "do": true,
        "like": true,
        "most": true,
        "about": true,
        "GraphQL?\\\"\\n": true,
        "\\\"Querying": true,
        "deeply": true,
        "nested": true,
        "objects": true,
        "😀\\\",\\n": true,
        "\\\"PrivacyConsent\\\",\\n": true,
        "\\\"descriptionHtml\\\":": true,
        "agree": true,
        "with": true,
        "our": true,
        "privacy": true,
        "policy?\\\"\\n": true,
        "\\\"consentGivenIndicator\\\":": true,
        "true\\n": true,
        "],\\n\\n": true,
        "overall": true,
        "score": true,
        "submission\\n": true,
        "1\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":107,\"column\":1,\"offset\":3770},\"end\":{\"line\":181,\"column\":4,\"offset\":5714},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  candidateProfile(id: $id) {
    seekQuestionnaireSubmission {
      questionnaire {
        id {
          value
        }
      }
      responses {
        componentTypeCode
        ... on ApplicationQuestionResponse {
          score
          component {
            value
            questionHtml
          }
          answers {
            answer
            choice {
              value
            }
          }
        }
        ... on ApplicationPrivacyConsentResponse {
          component {
            value
            descriptionHtml
          }
          consentGivenIndicator
        }
      }
      score
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;